/**
 * 画面スリープ防止機能
 * Wake Lock API + NoSleep.js による堅牢なスリープ防止
 */

export class WakeLockManager {
  constructor() {
    this.wakeLock = null
    this.noSleep = null
    this.fallbackVideo = null
    this.isActive = false
    this.fallbackMode = false
  }
  
  /**
   * スリープ防止開始
   */
  async start() {
    try {
      // 1. Wake Lock API を試行
      if ('wakeLock' in navigator) {
        await this.startWakeLock()
        if (this.isActive) {
          console.log('✅ Wake Lock API 有効化成功')
          return true
        }
      }
      
      // 2. NoSleep.js フォールバック
      await this.startNoSleep()
      if (this.isActive) {
        console.log('✅ NoSleep.js フォールバック有効化成功')
        this.fallbackMode = true
        return true
      }
      
      // 3. 代替手段（無音動画再生）
      await this.startFallback()
      if (this.isActive) {
        console.log('✅ 代替手段有効化成功')
        this.fallbackMode = true
        return true
      }
      
      // 4. 全て失敗
      console.warn('⚠️ スリープ防止機能が利用できません')
      return false
      
    } catch (error) {
      console.error('❌ スリープ防止開始エラー:', error)
      return false
    }
  }
  
  /**
   * Wake Lock API 開始
   */
  async startWakeLock() {
    try {
      this.wakeLock = await navigator.wakeLock.request('screen')
      this.isActive = true
      
      // ロック解放時の処理
      this.wakeLock.addEventListener('release', () => {
        console.log('⚠️ Wake Lock が解放されました')
        this.isActive = false
        this.wakeLock = null
      })
      
      return true
    } catch (error) {
      console.warn('⚠️ Wake Lock API 失敗:', error.message)
      return false
    }
  }
  
  /**
   * NoSleep.js 開始
   */
  async startNoSleep() {
    try {
      // NoSleep.js を動的インポート
      const NoSleepModule = await import('nosleep.js')
      const NoSleep = NoSleepModule.default || NoSleepModule
      this.noSleep = new NoSleep()
      
      // ユーザー操作で開始（ブラウザの制限回避）
      const startButton = document.createElement('button')
      startButton.style.display = 'none'
      document.body.appendChild(startButton)
      
      return new Promise((resolve) => {
        startButton.addEventListener('click', () => {
          try {
            // enable() は、画面を消灯させない許可が下りないと Promise を拒否する（iOSの低電力モード等）。
            // これまでどおり結果は待たずに進める（動作は変えない）。拒否だけを受け止めて、警告に残す
            // （受け止めないと、未処理の NotAllowedError として Sentry に出続ける）
            Promise.resolve(this.noSleep.enable()).catch((enableRejected) => {
              console.warn('⚠️ NoSleep.js 有効化が拒否されました:', enableRejected?.message || enableRejected)
            })
            this.isActive = true
            document.body.removeChild(startButton)
            resolve(true)
          } catch (enableError) {
            console.warn('⚠️ NoSleep.js 有効化失敗:', enableError.message)
            document.body.removeChild(startButton)
            resolve(false)
          }
        })
        
        // 自動クリック
        startButton.click()
      })
    } catch (error) {
      console.warn('⚠️ NoSleep.js インポート失敗:', error.message)
      return false
    }
  }
  
  /**
   * 代替手段（無音動画再生）
   */
  async startFallback() {
    try {
      // 無音動画を作成
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = 'black'
      ctx.fillRect(0, 0, 1, 1)
      
      const stream = canvas.captureStream(1) // 1fps
      const video = document.createElement('video')
      video.srcObject = stream
      video.muted = true
      video.loop = true
      video.style.display = 'none'
      
      document.body.appendChild(video)
      
      // 再生開始
      await video.play()
      this.isActive = true
      this.fallbackVideo = video
      
      console.log('✅ 代替手段（無音動画）有効化')
      return true
    } catch (error) {
      console.warn('⚠️ 代替手段失敗:', error.message)
      return false
    }
  }
  
  /**
   * スリープ防止停止
   */
  stop() {
    try {
      if (this.wakeLock) {
        this.wakeLock.release()
        this.wakeLock = null
      }
      
      if (this.noSleep) {
        this.noSleep.disable()
        this.noSleep = null
      }
      
      if (this.fallbackVideo) {
        this.fallbackVideo.pause()
        this.fallbackVideo.srcObject = null
        if (this.fallbackVideo.parentNode) {
          this.fallbackVideo.parentNode.removeChild(this.fallbackVideo)
        }
        this.fallbackVideo = null
      }
      
      this.isActive = false
      this.fallbackMode = false
      
      console.log('✅ スリープ防止停止')
    } catch (error) {
      console.error('❌ スリープ防止停止エラー:', error)
    }
  }
  
  /**
   * 状態取得
   */
  getStatus() {
    return {
      isActive: this.isActive,
      fallbackMode: this.fallbackMode,
      method: this.wakeLock ? 'wakeLock' : 
              (this.noSleep ? 'noSleep' : 
               (this.fallbackVideo ? 'fallback' : 'none'))
    }
  }
  
  /**
   * デバイス情報取得
   */
  getDeviceInfo() {
    const userAgent = navigator.userAgent
    const isIOS = /iPad|iPhone|iPod/.test(userAgent)
    const isAndroid = /Android/.test(userAgent)
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent)
    
    return {
      isIOS,
      isAndroid,
      isSafari,
      hasWakeLock: 'wakeLock' in navigator,
      userAgent
    }
  }
  
  /**
   * 低電力モード検出（iOS）
   */
  isLowPowerMode() {
    // iOSの低電力モードは直接検出できないため、ヒューリスティックで判定
    const deviceInfo = this.getDeviceInfo()
    if (!deviceInfo.isIOS) return false
    
    // バッテリーレベルが低い場合のヒント
    if ('getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        if (battery.level < 0.2) {
          console.warn('⚠️ バッテリーレベルが低いです（20%未満）')
        }
      })
    }
    
    return false // 直接検出は困難
  }
}
